import { useQuery } from "@apollo/client";
import { Box } from "@mui/material";
import _ from "lodash";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import {
  IntegrationServiceType,
  UsageOfSubaccountQuery,
  UsageOfSubaccountQueryVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import UsageBreakdownTable from "~frontend/components/usage/UsageBreakdownTable";
import UsageDateRangePicker from "~frontend/components/usage/UsageDateRangePicker";
import UsageTimeline from "~frontend/components/usage/UsageTimeline";
import { USAGE_OF_SUBACCOUNT } from "~frontend/graphql/usage";
import { useIntegrations } from "~frontend/hooks/useIntegrations";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";

function UsageData({
  startDate,
  endDate,
}: {
  startDate: DateTime;
  endDate: DateTime;
}) {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const { loading, error, data } = useQuery<
    UsageOfSubaccountQuery,
    UsageOfSubaccountQueryVariables
  >(USAGE_OF_SUBACCOUNT, {
    variables: {
      subaccountId: subaccount.id,
      startDate: startDate.toISODate()!,
      endDate: endDate.toISODate()!,
    },
  });

  useBreadcrumbs([
    {
      label: "Subaccount Usage",
      path: subaccountLink("/usage"),
    },
  ]);

  const {
    loading: integrationsLoading,
    error: integrationsError,
    hasIntegration: hasActBlueIntegration,
  } = useIntegrations({ service: IntegrationServiceType.ActBlueWebhook });

  if (loading || integrationsLoading) {
    return <Loading />;
  }

  if (error || integrationsError) {
    return <ErrorCard errors={[error || integrationsError]} />;
  }

  const donationTotals = {
    donationCount: _.sumBy(data!.usageOfSubaccountByCampaign, "donationCount"),
    recurringDonationCount: _.sumBy(
      data!.usageOfSubaccountByCampaign,
      "recurringDonationCount"
    ),
    amountRaisedInCents: _.sumBy(
      data!.usageOfSubaccountByCampaign,
      "amountRaisedInCents"
    ),
  };

  return (
    <>
      <UsageTimeline
        startDate={startDate}
        endDate={endDate}
        data={data!.usageOfSubaccountByDay}
        donationTotals={donationTotals}
        showFundraisingFields={hasActBlueIntegration}
      />
      <Box sx={{ marginTop: 6 }}>
        <h2>Campaigns</h2>
      </Box>

      <UsageBreakdownTable
        nameTitle="Campaign"
        rows={data!.usageOfSubaccountByCampaign.map(row => ({
          smsSegments: row.smsSegments,
          smsMessages: row.smsMessages,
          mmsMessages: row.mmsMessages,
          smsUnregisteredSegments: row.smsUnregisteredSegments,
          smsUnregisteredMessages: row.smsUnregisteredMessages,
          mmsUnregisteredMessages: row.mmsUnregisteredMessages,
          smsShortcodeSegments: row.smsShortcodeSegments,
          smsShortcodeMessages: row.smsShortcodeMessages,
          mmsShortcodeMessages: row.mmsShortcodeMessages,
          donationCount: row.donationCount,
          recurringDonationCount: row.recurringDonationCount,
          amountRaisedInCents: row.amountRaisedInCents,
          id: row.campaign?.id ?? "no-campaign",
          name: row.campaign?.name ?? "(no campaign)",
          link: row.campaign
            ? subaccountLink(`/campaigns/${row.campaign.shortId}`)
            : null,
          hasActBlueIntegration,
        }))}
        searchTitle="Find a campaign..."
        csvExportFileName={`${
          subaccount.name
        }_usage_by_campaign_${startDate.toISODate()}_${endDate.toISODate()}.csv`}
      />
    </>
  );
}

function validateStartDate(startDate: string): DateTime {
  const dt = DateTime.fromISO(startDate);
  if (!dt.isValid) {
    return DateTime.now().startOf("month");
  }
  return dt;
}

function validateEndDate(
  endDate: string,
  validatedStartDate: DateTime
): DateTime {
  const dt = DateTime.fromISO(endDate);
  if (!dt.isValid || dt < validatedStartDate) {
    return DateTime.now().endOf("month");
  }
  return dt;
}

export default function SubaccountUsage(): ReactElement {
  const [dateRange, setDateRange] = useQueryParams({
    start_date: withDefault(
      StringParam,
      DateTime.now().startOf("month").toISODate()
    ),
    end_date: withDefault(
      StringParam,
      DateTime.now().endOf("month").toISODate()
    ),
  });

  const validatedStartDate = validateStartDate(dateRange.start_date as string);
  const validatedEndDate = validateEndDate(
    dateRange.end_date as string,
    validatedStartDate
  );

  return (
    <PageContainer>
      <FlexBox justifyContent="space-between">
        <PageHeader>Usage</PageHeader>
        <UsageDateRangePicker
          value={{ startDate: validatedStartDate, endDate: validatedEndDate }}
          onChange={({ startDate, endDate }) => {
            setDateRange({
              start_date: startDate.toISODate()!,
              end_date: endDate.toISODate()!,
            });
          }}
        />
      </FlexBox>
      <UsageData startDate={validatedStartDate} endDate={validatedEndDate} />
    </PageContainer>
  );
}
